import { Toolbar } from '@mui/material';
import { ToolbarAction, ToolbarFilter } from '../../types/toolbar.types';
import { TableActionSection } from './TableActionsToolbar';
import { TableFilterSection } from './TableFilterToolbar';
import { useCallback } from 'react';

/**
 * Properties configuration contract for the EnhancedTableToolbar component.
 */
interface EnhancedTableToolbarProps {
  /** Core API backend orchestration data service powering action mutations */
  service: any;
  /** Declarative configuration schema list defining active layout filter widgets */
  filters?: ToolbarFilter[];
  /**
   * Global state mutation dispatch event listener.
   * Emits a flat key-value dictionary recording current user selections: { [filterId]: value }
   */
  onFiltersChange?: (activeFilters: Record<string, any>) => void;
  /** Layout schema definitions mapping out global utility context operations on the right */
  actions?: ToolbarAction[];
}

/**
 * Data-driven, presentation-agnostic wrapper toolbar designed for standard MUI tables.
 * Dynamically renders contextual filter layouts on the left and utility action anchors on the right.
 * Eliminates inline JSX boilerplate across individual presentation view layers.
 *
 * @param props - Core presentation layout parameters matching EnhancedTableToolbarProps configuration schema.
 * @returns Fully styled adaptive Material UI layout wrapper grid framework.
 */
export const EnhancedTableToolbar = ({
  service,
  filters = [],
  onFiltersChange,
  actions = []
}: EnhancedTableToolbarProps) => {
  if (filters.length === 0 && actions.length === 0) {
    return null;
  }

  const handleFiltersChange = useCallback(
    (activeFilters: Record<string, any>) => {
      if (onFiltersChange) {
        onFiltersChange(activeFilters);
      }
    },
    [onFiltersChange]
  );

  if (filters.length === 0 && actions.length === 0) {
    return null;
  }

  return (
    <Toolbar
      sx={{
        px: { xs: 0, sm: 0 },
        mb: 1,
        gap: 2,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        transition: 'all 0.2s ease',
        bgcolor: 'transparent'
      }}>
      <TableFilterSection
        filters={filters}
        onFiltersChange={handleFiltersChange}
        sx={{ flex: 1 }}
      />

      <TableActionSection service={service} actions={actions} />
    </Toolbar>
  );
};

export default EnhancedTableToolbar;
